import { useEffect, useState } from "react";

import axiosInstance from "../../api/axios.instance";
import { prospectRef } from "./prospectRef";

// Client-side state for the Video Library: collections, saved searches and
// the desk contact (name / email) reused across request forms.
// There is no backend for collections yet, so everything lives in
// localStorage and is synced between components through a window event.

const COLLECTIONS_KEY = "vb-library-collections";
const ACTIVE_KEY = "vb-library-active-collection";
const SAVED_SEARCHES_KEY = "vb-library-saved-searches";
const CONTACT_KEY = "vb-desk-contact";
const CHANGE_EVENT = "vb-library-store-change";

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.log(e);
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

const newId = () =>
  `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const DEFAULT_COLLECTION = () => ({
  id: newId(),
  name: "My collection",
  createdAt: Date.now(),
  items: [],
});

// Only the fields the library UI needs, so localStorage stays small
export const toClip = (element) => {
  const vd = element?.videoData || {};
  return {
    videoId: vd.videoId,
    title: vd.title,
    city: vd.city,
    country: vd.country,
    date: vd.date,
    duration: vd.duration,
    width: vd.width,
    height: vd.height,
    category: vd.category,
    exclusivity: !!element?.exclusivity,
    // small card thumbnail when generated (collections show many at once)
    screen:
      element?.bucket?.cloudThumbLink ||
      element?.bucket?.cloudScreenLink ||
      vd.screenLink ||
      null,
    preview:
      element?.bucket?.cloudPreviewLink ||
      element?.bucket?.cloudVideoLink ||
      null,
    note: "",
    addedAt: Date.now(),
  };
};

export const getCollections = () => {
  const list = read(COLLECTIONS_KEY, null);
  if (Array.isArray(list) && list.length) return list;
  const initial = [DEFAULT_COLLECTION()];
  try {
    localStorage.setItem(COLLECTIONS_KEY, JSON.stringify(initial));
  } catch (e) {}
  return initial;
};

export const getActiveCollectionId = () => {
  const list = getCollections();
  const id = read(ACTIVE_KEY, null);
  return list.some((c) => c.id === id) ? id : list[0].id;
};

const saveCollections = (list) => write(COLLECTIONS_KEY, list);

const updateCollection = (id, fn) =>
  saveCollections(getCollections().map((c) => (c.id === id ? fn(c) : c)));

export const collections = {
  setActive: (id) => write(ACTIVE_KEY, id),

  create: (name) => {
    const c = { ...DEFAULT_COLLECTION(), name: name || "Untitled collection" };
    saveCollections([...getCollections(), c]);
    write(ACTIVE_KEY, c.id);
    return c.id;
  },

  rename: (id, name) => updateCollection(id, (c) => ({ ...c, name })),

  remove: (id) => {
    const rest = getCollections().filter((c) => c.id !== id);
    saveCollections(rest.length ? rest : [DEFAULT_COLLECTION()]);
  },

  // Toggles a clip in the active collection, returns true when added
  toggle: (element) => {
    const activeId = getActiveCollectionId();
    const clip = toClip(element);
    let added = false;
    updateCollection(activeId, (c) => {
      const has = c.items.some((i) => i.videoId === clip.videoId);
      added = !has;
      return {
        ...c,
        items: has
          ? c.items.filter((i) => i.videoId !== clip.videoId)
          : [...c.items, clip],
      };
    });
    return added;
  },

  removeItem: (id, videoId) =>
    updateCollection(id, (c) => ({
      ...c,
      items: c.items.filter((i) => i.videoId !== videoId),
    })),

  moveItem: (id, index, dir) =>
    updateCollection(id, (c) => {
      const items = [...c.items];
      const to = index + dir;
      if (to < 0 || to >= items.length) return c;
      [items[index], items[to]] = [items[to], items[index]];
      return { ...c, items };
    }),

  setNote: (id, videoId, note) =>
    updateCollection(id, (c) => ({
      ...c,
      items: c.items.map((i) => (i.videoId === videoId ? { ...i, note } : i)),
    })),
};

// Saved search: { id, name, desc, params, createdAt, lastRunAt,
//   alert: null | "instant" | "hourly" | "daily" }  (alerts need a client account)
export const savedSearches = {
  list: () => read(SAVED_SEARCHES_KEY, []),
  add: (search) =>
    write(SAVED_SEARCHES_KEY, [
      { id: newId(), createdAt: Date.now(), lastRunAt: Date.now(), alert: null, ...search },
      ...savedSearches.list(),
    ]),
  update: (id, patch) =>
    write(
      SAVED_SEARCHES_KEY,
      savedSearches.list().map((s) => (s.id === id ? { ...s, ...patch } : s))
    ),
  markRun: (id) => savedSearches.update(id, { lastRunAt: Date.now() }),
  remove: (id) =>
    write(
      SAVED_SEARCHES_KEY,
      savedSearches.list().filter((s) => s.id !== id)
    ),
};

// Whole state, for syncing with a signed-in client account (clientSession.js)
let importing = false;
export const libraryState = {
  export: () => ({
    collections: getCollections(),
    activeId: getActiveCollectionId(),
    savedSearches: savedSearches.list(),
  }),
  import: (state) => {
    importing = true;
    try {
      if (Array.isArray(state.collections) && state.collections.length) {
        localStorage.setItem(COLLECTIONS_KEY, JSON.stringify(state.collections));
      }
      if (state.activeId) {
        localStorage.setItem(ACTIVE_KEY, JSON.stringify(state.activeId));
      }
      localStorage.setItem(
        SAVED_SEARCHES_KEY,
        JSON.stringify(Array.isArray(state.savedSearches) ? state.savedSearches : [])
      );
    } catch (e) {}
    window.dispatchEvent(new Event(CHANGE_EVENT));
    importing = false;
  },
  // Called on sign-out so the next person on this computer starts clean
  reset: () => {
    try {
      localStorage.removeItem(COLLECTIONS_KEY);
      localStorage.removeItem(ACTIVE_KEY);
      localStorage.removeItem(SAVED_SEARCHES_KEY);
    } catch (e) {}
    importing = true;
    window.dispatchEvent(new Event(CHANGE_EVENT));
    importing = false;
  },
  // fn runs on every user change (not on import/reset)
  subscribe: (fn) => {
    const handler = () => {
      if (!importing) fn();
    };
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  },
};

export const deskContact = {
  get: () => read(CONTACT_KEY, { name: "", email: "" }),
  set: (contact) => {
    try {
      localStorage.setItem(CONTACT_KEY, JSON.stringify(contact));
    } catch (e) {}
  },
};

const snapshot = () => ({
  collections: getCollections(),
  activeId: getActiveCollectionId(),
  savedSearches: savedSearches.list(),
});

// Re-renders the caller whenever library state changes (in this or another tab)
export const useLibraryStore = () => {
  const [state, setState] = useState(snapshot);

  useEffect(() => {
    const sync = () => setState(snapshot());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const active =
    state.collections.find((c) => c.id === state.activeId) ||
    state.collections[0];

  return {
    ...state,
    active,
    isInActive: (videoId) => active.items.some((i) => i.videoId === videoId),
  };
};

// Sends a form to the VBear licensing mailbox through the public /email
// endpoint (same one the Contact page uses). Limits follow its contract.
// desk: licensing | production | creators | copyright — the backend picks
// the inbox and escapes the text (plain text, one line per entry)
export const sendToDesk = async ({ name, email, subject, lines, desk }) => {
  const message = lines.filter(Boolean).join("\n").slice(0, 2000);

  const { data } = await axiosInstance.post("/email", {
    name: (name || "").trim().slice(0, 40),
    email: (email || "").trim(),
    subject: subject.slice(0, 100),
    message,
    ...(desk && { desk }),
    ...(prospectRef() && { prospect: prospectRef() }),
  });

  if (data.status !== "success") {
    throw new Error(data.message || "Failed to send");
  }

  deskContact.set({ name, email });
  return data;
};

export const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

// VB-REQ-XXXX style reference shown to the user after a request is sent
export const makeRef = (prefix) =>
  `${prefix}-${Date.now().toString(36).toUpperCase().slice(-6)}`;
